import { useEffect, useRef, useState } from "react"
import api from "../lib/api"

export type SuggestedVideo = {
  id: string
  title: string
  status: string
}

const STOPWORDS = new Set([
  "de", "la", "el", "los", "las", "en", "y", "con", "para", "un", "una", "unos", "unas",
  "a", "al", "del", "o", "e", "su", "sus", "que", "por", "sin", "es",
])

const DEBOUNCE_MS = 500
const MIN_TOKEN_LENGTH = 3

function stripAccents(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "")
}

/**
 * Splits the lesson title into candidate search tokens, longest/most-distinctive
 * first — cleaned of stopwords and short noise (version tags, numbers) so the
 * search against `GET /admin/videos?search=` has a real chance of matching a
 * filename-derived video title. Client-side only, see design.md decision 7 of
 * video-manager-lesson-association-improvements — kept as a thin layer over the
 * existing search endpoint so a future scored `/admin/videos/suggest` endpoint
 * can replace just this function without touching the picker.
 */
export function tokenizeForSuggestion(lessonTitle: string): string[] {
  const normalized = stripAccents(lessonTitle.toLowerCase())
  const tokens = normalized
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= MIN_TOKEN_LENGTH && !STOPWORDS.has(t))
  return [...tokens].sort((a, b) => b.length - a.length)
}

/**
 * Client-side "suggest a video for this lesson" — tries the longest token from
 * the lesson title first, falls back to the next one if no `ready` video
 * matches. Debounced so it doesn't fire on every keystroke while the staff is
 * still typing the title.
 */
export function useVideoSuggestion(lessonTitle: string): {
  suggestion: SuggestedVideo | null
  loading: boolean
} {
  const [suggestion, setSuggestion] = useState<SuggestedVideo | null>(null)
  const [loading, setLoading] = useState(false)
  const requestSeq = useRef(0)

  useEffect(() => {
    const tokens = tokenizeForSuggestion(lessonTitle)
    if (tokens.length === 0) {
      setSuggestion(null)
      setLoading(false)
      return
    }

    const seq = ++requestSeq.current
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        for (const token of tokens.slice(0, 3)) {
          const res = await api.get("/academy/admin/videos", {
            params: { search: token, status: "ready", limit: 5, page: 1 },
          })
          const videos: SuggestedVideo[] = res.data?.data?.videos ?? []
          if (seq !== requestSeq.current) return // superseded by a newer title change
          if (videos.length > 0) {
            setSuggestion(videos[0])
            setLoading(false)
            return
          }
        }
        if (seq === requestSeq.current) setSuggestion(null)
      } catch {
        if (seq === requestSeq.current) setSuggestion(null)
      } finally {
        if (seq === requestSeq.current) setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [lessonTitle])

  return { suggestion, loading }
}
